<template>
  <div class="asc-base-selector">
    <div
      :ref="`ascSelectorContainer${_uid}`"
      v-click-outside="handleClickOutsideBaseSelectorClick"
      :class="[
        'asc-base-selector--container',
        isProjectZeus && expanded ? 'asc-border--highlighted' : null,
        floatFilter ? 'asc-base-selector--container-float-filter' : null,
        readonly ? 'asc-base-selector--container-no-border' : null,
      ]"
    >
      <template v-if="!floatFilter">
        <template v-if="!noLabel && !labelBelow">
          <div
            :class="[
              'asc-base-selector--label',
              !expanded ? 'asc-font--normal' : null,
              isProjectZeus && expanded ? 'asc-font--highlighted' : null,
            ]"
            :style="label === ' ' ? { minHeight: '18px' } : {}"
          >
            <asc-required-label
              :label="label"
              :required="!disabled && !readonly ? required : false"
            ></asc-required-label>
          </div>
        </template>
        <div
          :class="[
            'asc-base-selector--input-container',
            isProjectZeus ? 'asc-base-selector--input-container-accent' : null,
          ]"
        >
          <div
            :class="[
              'asc-base-selector--input',
              !expanded ? 'asc-input--font-normal' : null,
              expanded ? 'asc-input--font-highlighted' : null,
              disabled || readonly ? 'asc-input--font--disabled' : null,
            ]"
            @click="handleArrowClick()"
          >
            <template v-if="isEmptyText">
              <div class="asc-base-selector--current-text">
                {{ getPlaceholder }}
              </div>
            </template>
            <template v-if="!isEmptyText">
              <div class="asc-base-selector--current-text">
                <v-icon v-if="icon">{{ icon }}</v-icon> {{ getCurrentText }}
              </div>
            </template>
          </div>
          <template v-if="!readonly">
            <div class="asc-base-selector--actions">
              <i
                aria-hidden="true"
                :class="[
                  'v-icon v-icon--link mdi mdi-chevron-down asc-menu-icon',
                  disabled || readonly ? 'asc-menu--disabled' : null,
                  isProjectZeus && (disabled || readonly)
                    ? 'asc-menu--disabled-accent'
                    : null,
                  isProjectZeus ? 'asc-menu-icon-accent' : null,
                  isProjectZeus && expanded
                    ? 'asc-menu--expanded-accent'
                    : null,
                  !isProjectZeus && expanded ? 'asc-menu--expanded' : null,
                ]"
                @click="handleArrowClick()"
              ></i>
            </div>
          </template>
        </div>
      </template>
      <template v-if="floatFilter">
        <div
          :class="[
            'asc-base-selector--input-container',
            isProjectZeus ? 'asc-base-selector--input-container-accent' : null,
            'asc-base-selector--input-container-compact',
          ]"
        >
          <div
            :class="[
              'asc-base-selector--input',
              'asc-base-selector--input-compact-font',
              !expanded ? 'asc-input--font-normal' : null,
              expanded ? 'asc-input--font-highlighted' : null,
              disabled || readonly ? 'asc-input--font--disabled' : null,
            ]"
            @click="handleArrowClick()"
          >
            <div class="asc-base-selector--current-text">
              <v-icon v-if="icon">{{ icon }}</v-icon> {{ getCurrentText }}
            </div>
          </div>
          <div class="asc-base-selector--actions">
            <i
              aria-hidden="true"
              :class="[
                'v-icon v-icon--link mdi mdi-filter asc-filter-icon',
                disabled || readonly ? 'asc-menu--disabled' : null,
                isProjectZeus && (disabled || readonly)
                  ? 'asc-menu--disabled-accent'
                  : null,
                isProjectZeus && expanded ? 'asc-filter--expanded' : null,
              ]"
              @click="handleArrowClick()"
            ></i>
          </div>
        </div>
      </template>
      <div
        v-if="expanded"
        :ref="`ascMenuWrapContainer${_uid}`"
        v-click-outside="handleClickOutsideFloatFilterClick"
        :class="[
          'asc-menu--wrap-container',
          fixedPosition ? 'asc-menu--wrap-container-fixed' : null,
          'asc-menu--wrap-container-padded',
        ]"
      >
        <div class="asc-menu--container">
          <slot />
        </div>
      </div>
    </div>
    <template v-if="!noLabel && labelBelow">
      <div
        :class="[
          'asc-base-selector--label',
          isProjectZeus ? 'asc-base-selector--label' : null,
          !expanded ? 'asc-font--normal' : null,
          isProjectZeus && expanded ? 'asc-font--highlighted' : null,
        ]"
      >
        <span>{{ label }}</span>
      </div>
    </template>
  </div>
</template>

<script>
import AscRequiredLabel from '@/components/atoms/AscRequiredLabel'
import AppFeatureFlags from '@/utilities/appFeatureFlags'

export default {
  name: 'AscBaseSelector',

  components: {
    AscRequiredLabel,
  },

  props: {
    currentText: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: 'Select',
    },
    placeholder: {
      type: String,
      default: '',
    },
    floatFilter: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    autosize: {
      type: Boolean,
      default: false,
    },
    noLabel: {
      type: Boolean,
      default: false,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      default: '',
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    labelBelow: {
      type: Boolean,
      default: false,
    },
    noAutoClose: {
      type: Boolean,
      default: false,
    },
    noPlaceholder: {
      type: Boolean,
      default: false,
    },
    dropDownSizeMatches: {
      type: Boolean,
      default: false,
    },
    selectedItems: {
      type: Array,
      default: () => [],
    },
    required: {
      type: Boolean,
      default: false,
    },
    fixedPosition: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: String,
      default: undefined,
    },
    items: {
      type: Array,
      default: () => [],
    },
    dateSelector: {
      type: Boolean,
      default: false,
    },
    onClose: {
      type: Function,
      default: () => {},
    },
    onExpandMenu: {
      type: Function,
      default: () => {},
    },
    onCollapseMenu: {
      type: Function,
      default: () => {},
    },
  },

  data() {
    return {
      expanded: false,
    }
  },

  computed: {
    isProjectZeus() {
      return AppFeatureFlags.getProjectZeus()
    },

    isEmptyText() {
      return this.currentText === ''
    },

    getCurrentText() {
      if (
        this.floatFilter &&
        !this.dateSelector &&
        this.items?.length === this.selectedItems?.length
      ) {
        return ''
      }
      return this.currentText
    },

    getMinCurrentText() {
      if (this.getCurrentText === this.placeholder) {
        return this.noPlaceholder ? '' : this.placeholder
      }
      return this.getCurrentText
    },

    getPlaceholder() {
      return this.noPlaceholder ? '' : this.placeholder
    },
  },

  watch: {
    currentText() {
      if (!this.multiple && this.expanded && !this.noAutoClose) {
        this.handleArrowClick()
      }
    },

    expanded(newVal, oldVal) {
      if (newVal !== oldVal) {
        if (newVal) {
          if (this.onExpandMenu) {
            this.onExpandMenu()
          }
        }
        if (!newVal) {
          if (this.onCollapseMenu) {
            this.onCollapseMenu()
          }
        }
      }
    },
  },

  methods: {
    handleArrowClick() {
      if (this.disabled || this.readonly) {
        return
      }
      this.expanded = !this.expanded
      if (this.expanded) {
        setTimeout(() => {
          const gridHeaderHeight = 80
          const paddingHeight = 6
          const inputContainer = this.$refs[`ascSelectorContainer${this._uid}`]
          const inputRect = inputContainer.getBoundingClientRect()
          const popup = this.$refs[`ascMenuWrapContainer${this._uid}`]
          if (popup) {
            if (this.autosize) {
              if (this.dense) {
                popup.style.width = `${inputRect.width}px`
              } else {
                popup.style.width =
                  inputRect.width > 275 ? `${inputRect.width}px` : '275px'
              }
            }
            if (this.floatFilter) {
              const rootWrapperParent = document.getElementById(
                this.$parent.$parent.$parent.$el.id,
              )

              const rootWrappers = rootWrapperParent
                ? rootWrapperParent.getElementsByClassName('ag-root-wrapper')
                : document.getElementsByClassName('ag-root-wrapper')

              if (rootWrappers.length > 0) {
                const rootWrapper = rootWrappers[0]
                const gridRootWrapperRect = rootWrapper.getBoundingClientRect()
                const gridRootWrapperLeft = gridRootWrapperRect.left
                const popupLeft = `${inputRect.left - gridRootWrapperLeft}px`
                const popupTop = `${gridHeaderHeight - paddingHeight}px`
                popup.style.left = popupLeft
                popup.style.top = popupTop
                rootWrapper.appendChild(popup)
              }
            }
            setTimeout(() => {
              if (popup && popup.offsetParent) {
                const rootWrapperParent = document.getElementById(
                  this.$parent.$parent.$parent.$el.id,
                )

                const rootWrappers = rootWrapperParent
                  ? rootWrapperParent.getElementsByClassName('ag-root-wrapper')
                  : document.getElementsByClassName('ag-root-wrapper')

                if (rootWrappers.length > 0) {
                  const rootWrapper = rootWrappers[0]
                  const gridRootWrapperRect =
                    rootWrapper.getBoundingClientRect()
                  const rootWrapperRight = gridRootWrapperRect.right
                  const inputRectLeft = inputRect.left
                  const popupWidth = popup.offsetWidth
                  const isNeedsOffsetFromRight =
                    inputRectLeft + popupWidth + 12 > rootWrapperRight

                  if (isNeedsOffsetFromRight && !this.dropDownSizeMatches) {
                    popup.style.left = this.floatFilter ? 'auto' : 0
                    popup.style.right = '14px'
                  }
                }
              }
            }, 0)
          }
        }, 0)
      }
      if (!this.expanded) {
        if (this.onClose) {
          this.onClose()
        }
      }
    },

    handleClickOutsideBaseSelectorClick(event) {
      if (!this.floatFilter) {
        if (this.fixedPosition && this.expanded) {
          event.stopPropagation()
        }
        if (this.expanded) {
          this.expanded = false
        }
        if (!this.expanded) {
          if (this.onClose) {
            this.onClose()
          }
        }
      }
    },

    handleClickOutsideFloatFilterClick() {
      if (this.floatFilter) {
        if (this.expanded) {
          this.expanded = false
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.asc-base-selector {
  padding: 0;
  width: 100%;

  & .asc-base-selector--current-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-top: 2px;
  }
}

.asc-menu-icon {
  cursor: pointer;
  color: $gray-600;
}

.asc-menu-icon-accent {
  &:hover {
    color: $accent-500;
  }
}

.asc-menu--expanded {
  transform: rotate(-180deg);
}

.asc-menu--expanded-accent {
  transform: rotate(-180deg);
  color: $accent-500;

  &::before {
    color: $accent-500 !important;
  }
}

.asc-menu--disabled {
  color: $gray-500 !important;
  cursor: not-allowed !important;

  &:hover {
    opacity: 1 !important;
  }
}

.asc-menu--disabled-accent {
  color: $gray-500 !important;
}

.asc-filter-icon {
  cursor: pointer;
  color: $gray-700;
  font-size: 16px;
}

.asc-font--normal {
  color: $gray-700 !important;
}

.asc-input--font--disabled {
  cursor: not-allowed !important;

  &:hover {
    opacity: 1 !important;
  }
}

.asc-base-selector--container {
  opacity: 1;
  width: 100%;
  border-bottom: 1px solid $gray-400;
  position: relative;

  &:hover:not(.asc-border--highlighted) {
    border-bottom-color: $primary-black;
  }
}

.asc-base-selector--container-float-filter {
  border-bottom: 1px solid $gray-500;

  .asc-base-selector--actions {
    .asc-filter-icon {
      color: $gray-500;
    }
  }

  &:hover {
    .asc-base-selector--actions {
      .asc-filter-icon {
        color: $primary-black;
      }
    }
  }
}

.asc-base-selector--container-no-border {
  border-bottom: none;
}

.asc-base-selector--label {
  font-size: $font-size-sm;
  letter-spacing: initial;
}

.asc-base-selector--label-accent {
  color: $gray-700;
}

.asc-base-selector--input-container {
  display: flex;
  width: 100%;
  height: 24px;

  &:hover .mdi-chevron-down {
    color: $primary-black;
  }
}

.asc-base-selector--input-container-accent {
  &:hover .mdi-chevron-down {
    color: $primary-black;
  }
}

.asc-base-selector--input-container-compact {
  height: 22px;
}

.asc-base-selector--input {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 14px;

  &:hover {
    cursor: pointer;
  }
}

.asc-base-selector--input-compact-font {
  font-size: $font-size-sm;
}

.asc-input--font-normal {
  color: $primary_black;
  letter-spacing: initial;
}

.asc-menu--wrap-container {
  position: absolute;
  z-index: 9999;
  background-color: $primary_white;
  display: block;
  overflow: hidden;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.17);
  margin-top: 2px;
}

.asc-menu--wrap-container-fixed {
  position: fixed;
}

.asc-menu--wrap-container-padded {
  padding-bottom: 4px;
}

.asc-menu--container {
  align-items: flex-start;
  display: flex;
  flex: 1 1 auto;
}

.asc-font--highlighted span {
  color: $accent-700 !important;
}

.asc-filter--expanded {
  color: $accent-500 !important;
}

.asc-border--highlighted {
  border-bottom: 2px solid $accent-600;
}
</style>
